import React, {useEffect, useMemo, useRef, useState} from 'react';
import GRPC_BACKEND_URL, {GRPC_CLIENT_OPTIONS} from "../grpcConfig";
import {UserData, UserDatas} from "../generated/user_pb";
import dayjs from "dayjs";
import {ScorerClient} from "../generated/score_grpc_web_pb";
import {BodypartScore, UserScore, UserScoreBodypart, UserScores} from "../generated/score_pb";
import ReusableChartCard from "../Components/ReusableChart";
import {ErrorState, PageHeader} from '@dowty/ui';
import {chartColor} from '../utils/chartColors';
import {isGerman} from '../utils/language';

type ScorePart = {
    date: string;
    score: number;
};

type scoreObj = {
    username: string,
    scores: ScorePart[];
};
export type ChartDataPoint = {
    date: string;
    [username: string]: number | string | undefined;
};

function mapBodypartScore(score: BodypartScore): ScorePart {
    return { score: score.getScore(), date: score.getBodypartname() };
}

const entry: scoreObj[] = [
    {
        username: 'lol',
        scores: [
            {date: '2025-05-01', score: 20},
            {date: '2025-05-02', score: 30},
            {date: '2025-05-03', score: 25},
            {date: '2025-05-04', score: 40},
            {date: '2025-05-05', score: 100}
        ],
    },
    {
        username: 'User 2',
        scores: [
            {date: '2025-05-01', score: 35},
            {date: '2025-05-02', score: 45},
            {date: '2025-05-03', score: 50},
            {date: '2025-05-04', score: 60},
            {date: '2025-05-05', score: 0}
        ],
    },
    {
        username: 'User 3',
        scores: [
            {date: '2025-05-01', score: 15},
            {date: '2025-05-02', score: 25},
            {date: '2025-05-03', score: 20},
            {date: '2025-05-04', score: 30}
        ],
    }
];


export const ArchivedScoresPage: React.FC<{theme: string}> = ({theme}) => {
    const [selectedUsers, setSelectedUsers] = useState<string[]>(['User 1']); // Default to User 1
    const scorerClient = useMemo(() => new ScorerClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS), []);
    const [userScores, setUserScores] = useState<scoreObj[]>(entry);
    const [selectableUsers, setSelectableUsers] = useState<string[]>([]);
    const [userMuscles, setUserMuscles] = useState<scoreObj[]>(entry);
    const [userMusclesGroups, setUserMusclesGroups] = useState<scoreObj[]>(entry);
    const [scoreLoadError, setScoreLoadError] = useState(false);

    async function fetchScores() {
        setScoreLoadError(false);
        const userString = localStorage.getItem('user');
        const friendsString = localStorage.getItem('friends');
        if (!userString) {
            return;
        }

        const user = UserData.deserializeBinary(Uint8Array.from(atob(userString), c => c.codePointAt(0)!));
        let friends: UserDatas = new UserDatas()
        if (friendsString) {
            friends = UserDatas.deserializeBinary(Uint8Array.from(atob(friendsString), c => c.codePointAt(0)!));
        }

        const allUsers = [user, ...friends.getUserList()];
        setSelectedUsers([user.getUsername()]);
        const usernames = allUsers.map(u => u.getUsername());
        setSelectableUsers(usernames);

        const scoresByUser: scoreObj[] = [];
        // let codeUglifier: CozBabyTonight[] = [] this is legacy code... I am sad it does not exist no longer

        loadMusclePoints(user, allUsers);
        const fetchUserScore = (userData: UserData): Promise<void> => {
            return new Promise((resolve) => {
                const request = new UserScores();
                request.setUser(user);
                const target = new UserScore();
                target.setUser(userData);
                request.setUsersscoresList([target]);
                request.setDaylength(7);

                scorerClient.getUserScore(request, undefined, (err, res) => {
                    if (err) {
                        setScoreLoadError(true);
                        return resolve(); // or reject(err) if you want to stop everything
                    }

                    if (res) {
                        const dailyScores: ScorePart[] = []

                        for (const score of res.getUsersscoresList()) {
                            const scoreday = score.getScoreday();
                            if (!scoreday) continue;
                            dailyScores.push({
                                score: score.getScore(),
                                date: dayjs(scoreday.toDate()).format("YYYY-MM-DD"),
                            })
                        }
                        scoresByUser.push({
                            username: userData.getUsername(),
                            scores: dailyScores
                        })
                    }
                    resolve();
                });
            });
        };

        await Promise.all(allUsers.map(fetchUserScore));

        setUserScores(scoresByUser);
    };

    async function loadMusclePoints(user: UserData, userDatas: UserData[]) {
        const scoresByUser: scoreObj[] = [];
        const muscleGroupScores: scoreObj[] = [];

        const buildRequest = (userData: UserData): UserScores => {
            const request = new UserScores();
            request.setUser(user);
            const useless = new UserScore();
            useless.setUser(userData);
            request.setUsersscoresList([useless]);
            request.setDaylength(7);
            return request;
        };

        const fetchMuscleScore = (userData: UserData, muscleGroup: boolean): Promise<void> =>
            new Promise((resolve) => {
                const onResult = (err: unknown, res?: UserScoreBodypart) => {
                    if (err) setScoreLoadError(true);
                    if (!err && res) {
                        (muscleGroup ? muscleGroupScores : scoresByUser).push({
                            username: userData.getUsername(),
                            scores: res.getBodypartscoresList().map(mapBodypartScore),
                        });
                    }
                    resolve();
                };
                const request = buildRequest(userData);
                if (muscleGroup) scorerClient.getUsersMusclegroupScores(request, undefined, onResult);
                else scorerClient.getUsersMuscleScores(request, undefined, onResult);
            });

        await Promise.all([
            ...userDatas.map(userData => fetchMuscleScore(userData, false)),
            ...userDatas.map(userData => fetchMuscleScore(userData, true)),
        ]);

        setUserMuscles(scoresByUser);
        setUserMusclesGroups(muscleGroupScores);
    }

    function ouPapiWheresMyColor(username: string) {
        return chartColor(selectableUsers.indexOf(username), theme === 'dark' ? 'dark' : 'light');
    }



    const fetchScoresOnMount = useRef(fetchScores);
    useEffect(() => {
        fetchScoresOnMount.current();
    }, []);

    function transformMeDaddy(userList: scoreObj[], sortByDate = true) {
        const dateMap: Record<string, ChartDataPoint> = {};
        userList.forEach((user) => {
            user.scores.forEach(({date, score}) => {
                if (!dateMap[date]) {
                    dateMap[date] = {date};
                }
                dateMap[date][user.username] = score;
            });
        });

        const points = Object.values(dateMap);
        return sortByDate ? points.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) : points;
    }


    const handleUserChange = (value: string[]) => {
        setSelectedUsers(value);
    };


    return (
        <div className="sf-statistics-page">
            <PageHeader title={isGerman() ? 'Statistiken' : 'Statistics'} subtitle="Your archived scores and training points" />
            {scoreLoadError && <ErrorState
                message={isGerman() ? 'Statistiken konnten nicht vollständig geladen werden.' : 'Statistics could not be fully loaded.'}
                retry={() => void fetchScores()}
                retryLabel={isGerman() ? 'Erneut versuchen' : 'Retry'}
            />}
            <ReusableChartCard
                selectedUsers={selectedUsers}
                selectableUsers={selectableUsers}
                showData={transformMeDaddy(userScores)}
                ouPapiWheresMyColor={ouPapiWheresMyColor}
                lineChartInclude={true}
                handleUserChange={handleUserChange}
                headerText={"Archived scores last week"}
                theme={theme}
            />
            <ReusableChartCard
                selectedUsers={selectedUsers}
                handleUserChange={handleUserChange}
                selectableUsers={selectableUsers}
                showData={transformMeDaddy(userMusclesGroups, false)}
                ouPapiWheresMyColor={ouPapiWheresMyColor}
                lineChartInclude={false}
                headerText={"Archived points by muscle group"}
                theme={theme}
            />
            <ReusableChartCard
                selectedUsers={selectedUsers}
                handleUserChange={handleUserChange}
                selectableUsers={selectableUsers}
                showData={transformMeDaddy(userMuscles, false)}
                ouPapiWheresMyColor={ouPapiWheresMyColor}
                lineChartInclude={false}
                headerText={"Archived points by muscle"}
                theme={theme}
            />
        </div>
    );
};
